import { UUID } from 'crypto'
import Users from '../../../../domain/user/Users'
import UserApi from '../../api/UserApi'
import { UserContextType } from '../UserContext'
import { UserReducerType } from '../reducer/UserReducerType'
import User from '../../../../domain/user/User'
import UpdateStudent from '../../../../domain/user/UpdateStudent'
import { CreateUserUI } from '../../model/CreateUserUI'
import CreateUser from '../../../../domain/user/CreateUser'
import UpdateLicense from '../../../../domain/license/UpdateLicense'
import { UpdateStudentUI } from '../../model/UpdateStudentUI'
import { UpdateLicenseUI } from '../../model/UpdateLicenseUI'
import CreateDebt from '../../../../domain/payment/CreateDebt'
import CreatePayment from '../../../../domain/payment/CreatePayment'
import PaymentTransactionsApi from '../../api/PaymentTransactionsApi'

class UserAction {
  constructor(context: UserContextType) {
    this.context = context
  }

  private readonly context: UserContextType

  private cleanUsers(): void {
    this.context.dispatch({ type: UserReducerType.CLEAN_ALL_USERS })
  }

  private setEditMode(editMode: boolean): void {
    this.context.dispatch({
      type: UserReducerType.SET_EDIT_MODE,
      payload: editMode,
    })
  }

  private setIsValid(valid: boolean): void {
    this.context.dispatch({
      type: UserReducerType.SET_IS_VALID,
      payload: valid,
    })
  }

  async setUsers(): Promise<Users> {
    if (!this.context.state.allUsers.isEmpty()) {
      return this.context.state.allUsers
    }

    const users = await UserApi.fetchUsers(this.context.keycloak)
    this.context.dispatch({
      type: UserReducerType.SET_ALL_USERS,
      payload: users,
    })

    return users
  }

  async setStudents(): Promise<void> {
    if (!this.context.state.allStudents.isEmpty()) {
      return
    }

    const users = await this.setUsers()
    const students = users.getStudents()

    this.context.dispatch({
      type: UserReducerType.SET_ALL_STUDENTS,
      payload: students,
    })
  }

  async removeStudent(id: UUID): Promise<void> {
    await UserApi.removeStudent(this.context.keycloak, id)
  }

  async createNewDebt(): Promise<void> {
    const { currentStudent, createDebt } = this.context.state

    if (currentStudent !== undefined && createDebt !== undefined) {
      await PaymentTransactionsApi.createDebt(
        this.context.keycloak,
        currentStudent.getStudentId(),
        createDebt,
      )
    }

    await this.refreshCurrentStudent()
  }

  async removeDebt(id: UUID): Promise<void> {
    await PaymentTransactionsApi.removeDebt(this.context.keycloak, id)
    await this.refreshCurrentStudent()
  }

  async createNewPayment(): Promise<void> {
    const { currentStudent, createPayment } = this.context.state

    if (currentStudent !== undefined && createPayment !== undefined) {
      await PaymentTransactionsApi.createPayment(
        this.context.keycloak,
        currentStudent.getStudentId(),
        createPayment,
      )
    }

    await this.refreshCurrentStudent()
  }

  async removePayment(id: UUID): Promise<void> {
    await PaymentTransactionsApi.removePayment(this.context.keycloak, id)
    await this.refreshCurrentStudent()
  }

  async refreshCurrentStudent(): Promise<void> {
    const currentStudent = this.getCurrentStudent()

    if (currentStudent !== undefined) {
      const student = await UserApi.fetchUser(
        this.context.keycloak,
        currentStudent.getId(),
      )

      this.context.dispatch({
        type: UserReducerType.SET_CURRENT_STUDENT,
        payload: student,
      })
    }
  }

  getUsers(): Users {
    return this.context.state.allUsers
  }

  getStudents(): Users {
    return this.context.state.allStudents
  }

  getCurrentStudent(): User | undefined {
    return this.context.state.currentStudent
  }

  isEditMode(): boolean {
    return this.context.state.editMode
  }

  toggleEditMode(): void {
    this.setEditMode(!this.context.state.editMode)
  }

  setCurrentStudentById(studentId: UUID): void {
    const student = this.context.state.allStudents.findStudentById(studentId)
    this.context.dispatch({
      type: UserReducerType.SET_CURRENT_STUDENT,
      payload: student,
    })
  }

  setCreateUser(createUserUI: CreateUserUI): void {
    const createUser = CreateUser.fromCreateUserUI(createUserUI)

    this.context.dispatch({
      type: UserReducerType.SET_CREATE_USER,
      payload: createUser,
    })

    if (createUser === undefined) {
      this.setIsValid(false)
    } else {
      this.setIsValid(createUser.isValid())
    }
  }

  updateStudentFromCurrent(): UpdateStudentUI | undefined {
    const student = this.getCurrentStudent()
    const updateStudentUI =
      student !== undefined
        ? UpdateStudent.fromStudent(student).toUI()
        : undefined

    this.setUpdateStudent(updateStudentUI)

    return updateStudentUI
  }

  setUpdateStudent(updateStudentUI?: UpdateStudentUI): void {
    const updateStudent =
      updateStudentUI !== undefined
        ? UpdateStudent.fromStudentUI(updateStudentUI)
        : undefined

    this.context.dispatch({
      type: UserReducerType.SET_UPDATE_STUDENT,
      payload: updateStudent,
    })

    if (updateStudent === undefined) {
      this.setIsValid(false)
    } else {
      this.setIsValid(updateStudent.isValid())
    }
  }

  getCreateUser(): CreateUser | undefined {
    return this.context.state.createUser
  }

  async updateStudent(): Promise<void> {
    const { currentStudent, updateStudent } = this.context.state
    const studentId = currentStudent?.getStudentId()

    if (
      studentId !== undefined &&
      updateStudent !== undefined &&
      updateStudent.isValid()
    ) {
      await UserApi.updateStudent(
        this.context.keycloak,
        studentId,
        updateStudent,
      )
    }
  }

  async createStudent(): Promise<void> {
    const { createUser } = this.context.state

    if (createUser !== undefined && createUser.isValid()) {
      await UserApi.createStudent(this.context.keycloak, createUser)
    }
  }

  isValid(): boolean {
    return this.context.state.isValid
  }

  refreshStudentsPage(): void {
    this.cleanUsers()
    this.setEditMode(false)
  }

  isLicenseSelected(): boolean {
    return this.context.state.selectedLicense !== undefined
  }

  private findLicense(licenseId: string): UpdateLicense {
    if (this.context.state.updateStudent !== undefined) {
      return this.context.state.updateStudent.findLicense(licenseId)
    }

    throw Error('Error here...')
  }

  getSelectedLicense(): UpdateLicense | undefined {
    return this.context.state.selectedLicense
  }

  deselectLicense(): void {
    this.context.dispatch({
      type: UserReducerType.SET_SELECTED_LICENSE,
      payload: undefined,
    })
  }

  selectLicense(licenseId: string): void {
    const license = this.findLicense(licenseId)

    this.context.dispatch({
      type: UserReducerType.SET_SELECTED_LICENSE,
      payload: license,
    })
  }

  updateSelectedLicense(updateLicenseUI: UpdateLicenseUI): void {
    const { updateStudent } = this.context.state
    const license = this.getSelectedLicense()

    if (updateStudent !== undefined && license !== undefined) {
      const updateLicense = UpdateLicense.fromUpdateLicenseUI(updateLicenseUI)
      updateStudent.updateLicense(updateLicense)
    }
  }

  addLicense(updateLicenseUI: UpdateLicenseUI) {
    const updateLicense = UpdateLicense.fromUpdateLicenseUI(updateLicenseUI)
    const { updateStudent } = this.context.state

    updateStudent?.addLicense(updateLicense.getId())

    this.context.dispatch({
      type: UserReducerType.SET_UPDATE_STUDENT,
      payload: updateStudent,
    })
  }

  removeLicense(updateLicenseUI: UpdateLicenseUI) {
    const updateLicense = UpdateLicense.fromUpdateLicenseUI(updateLicenseUI)
    const { updateStudent } = this.context.state

    updateStudent?.removeLicense(updateLicense.getId())

    this.context.dispatch({
      type: UserReducerType.SET_UPDATE_STUDENT,
      payload: updateStudent,
    })
  }

  getUpdateStudent(): UpdateStudent | undefined {
    return this.context.state.updateStudent
  }

  setCreateDebt(debt: number, description: string): void {
    const createDebt = new CreateDebt(debt, description)

    this.context.dispatch({
      type: UserReducerType.SET_CREATE_DEBT,
      payload: createDebt,
    })
  }

  setCreatePayment(paid: number, description: string): void {
    const createPayment = new CreatePayment(paid, description)

    this.context.dispatch({
      type: UserReducerType.SET_CREATE_PAYMENT,
      payload: createPayment,
    })
  }
}

export default UserAction
